import { motion } from "framer-motion";
import { ClipboardList, FileQuestion, Headphones, BarChart3 } from "lucide-react";
import { SectionHeader } from "./Services";

const STEPS = [
    {
        n: "01",
        icon: ClipboardList,
        title: "Project Brief",
        desc: "We align on objectives, audience, sample, geography and timelines — anchored to a measurable outcome.",
    },
    {
        n: "02",
        icon: FileQuestion,
        title: "Questionnaire Design",
        desc: "Behavioural science meets clean logic. Routing, quotas and translations are baked in.",
    },
    {
        n: "03",
        icon: Headphones,
        title: "CATI Data Collection",
        desc: "Trained interviewers + live supervision + voice QC. Daily progress on a real-time dashboard.",
    },
    {
        n: "04",
        icon: BarChart3,
        title: "Analysis & Reporting",
        desc: "From cross-tabs to story-driven decks — we surface the 3 things you actually need to act on.",
    },
];

export default function Process() {
    return (
        <section
            id="process"
            className="relative py-28 bg-[#040A18] border-y border-white/5"
            data-testid="process-section"
        >
            <div className="absolute inset-0 bg-grid bg-grid-fade opacity-25 pointer-events-none" />
            <div className="pr-container relative">
                <SectionHeader
                    overline="Process"
                    title="A futuristic flow, end to end"
                    subtitle="Four steps. Continuously instrumented. Always traceable."
                />

                <div className="mt-16 relative">
                    {/* desktop horizontal line */}
                    <svg
                        className="absolute top-[58px] left-0 right-0 h-1 hidden lg:block"
                        viewBox="0 0 1200 4"
                        preserveAspectRatio="none"
                    >
                        <defs>
                            <linearGradient id="proc-grad" x1="0" x2="1">
                                <stop offset="0%" stopColor="#3B82F6" />
                                <stop offset="100%" stopColor="#22C55E" />
                            </linearGradient>
                        </defs>
                        <motion.line
                            x1="40"
                            x2="1160"
                            y1="2"
                            y2="2"
                            stroke="url(#proc-grad)"
                            strokeWidth="2"
                            initial={{ pathLength: 0 }}
                            whileInView={{ pathLength: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 2.2, ease: "easeInOut" }}
                        />
                    </svg>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <motion.div
                                    key={s.n}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{
                                        duration: 0.6,
                                        delay: i * 0.12,
                                    }}
                                    className="relative"
                                    data-testid={`process-step-${i}`}
                                >
                                    <div className="flex flex-col items-center">
                                        <div className="relative">
                                            <div className="w-28 h-28 rounded-full pr-glass-strong border border-blue-400/30 flex items-center justify-center relative z-10">
                                                <Icon className="w-9 h-9 text-blue-300" />
                                            </div>
                                            <span className="absolute -top-2 -right-2 z-20 w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center font-mono-pr text-[11px] font-bold text-emerald-300">
                                                {s.n}
                                            </span>
                                        </div>

                                        <h3 className="mt-6 font-outfit text-lg font-semibold text-white text-center">
                                            {s.title}
                                        </h3>
                                        <p className="mt-2 text-sm text-slate-400 leading-relaxed text-center max-w-[260px]">
                                            {s.desc}
                                        </p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
